import { EStorePhase, ESupportType } from '@/lib/enums';
import type { BootData } from '@/lib/types';
import { ensureAbsoluteUrl } from '@/lib/utils';

interface SupportParams {
  playerIdParamName?: string;
  playerNameParamName?: string;
}

interface StoreFooterLink {
  key: string;
  label: string;
  url?: string;
  type?: 'footer' | 'store';
  config?: SupportParams & { isCustom?: boolean };
}

interface StoreFooter {
  links?: StoreFooterLink[];
  openLinksInNewTab?: boolean;
  privacyPolicyUrl?: string;
  termsAndConditionsUrl?: string;
  supportLink?: string;
  supportParams?: SupportParams;
  enableRefundFooterLink?: boolean;
  enabledRefundFooterLinkCountries?: string[];
}

export interface FooterPlayer {
  playerId?: string;
  playerName?: string;
  playerCountry?: string;
}

export type FooterLinkTarget = 'page' | 'newTab' | 'modal';

export interface PortalFooterLink {
  key: string;
  label: string;
  translationKey: string;
  url: string;
  target: FooterLinkTarget;
}

interface FooterLinkContext {
  bootData: BootData;
  footer: StoreFooter;
  player: FooterPlayer | null;
  linkTarget: FooterLinkTarget;
}

const STORE_LINK_KEYS = ['appstoreLink', 'googlePlayLink'];
const EU_REFUND_LINK_FLAG = 'eu_refund_link_enabled';
const FOOTER_LINKS_MANAGER_FLAG = 'Dashboard_footer_links_manager';

const REFUND_BASE_URL = 'https://help.appcharge.com/hc/en-us/requests/new';
const REFUND_TOPIC_PARAM = 'tf_25438680794909';
const REFUND_TOPIC_VALUE = 'b2c_ticket_topic_refund';
const REFUND_PLAYER_ID_PARAM = 'tf_25438695019421';
const REFUND_PROJECT_ID_PARAM = 'tf_25438782685085';
const REFUND_COUNTRY_PARAM = 'tf_25438841537437';

const isStoreLink = (link: StoreFooterLink): boolean =>
  link.type === 'store' || STORE_LINK_KEYS.includes(link.key);

/**
 * The footer links the connected store renders, in the same order and with the
 * same visibility rules, or `undefined` while there is no store boot.
 */
export const resolveStoreFooterLinks = (
  bootData: BootData | undefined,
  player: FooterPlayer | null
): PortalFooterLink[] | undefined => {
  if (!bootData?.storeTheme) return undefined;

  const footer = resolveStoreFooter(bootData);
  const context: FooterLinkContext = {
    bootData,
    footer,
    player,
    linkTarget: footer.openLinksInNewTab ? 'newTab' : 'modal',
  };
  const configuredLinks = (footer.links ?? []).filter((link) => !isStoreLink(link));
  const links = configuredLinks.length
    ? configuredLinks.map((link) => toConfiguredLink(link, context))
    : toDefaultLinks(context);

  return links.filter((link): link is PortalFooterLink => Boolean(link));
};

const resolveStoreFooter = (bootData: BootData): StoreFooter => {
  const general = (bootData.storeTheme.general?.footer ?? {}) as StoreFooter;
  const override = bootData.storeTheme.storeSettings?.footer as StoreFooter | undefined;
  if (!override) return general;
  return {
    ...general,
    ...override,
    privacyPolicyUrl: override.privacyPolicyUrl ?? general.privacyPolicyUrl,
    termsAndConditionsUrl: override.termsAndConditionsUrl ?? general.termsAndConditionsUrl,
    openLinksInNewTab: override.openLinksInNewTab ?? general.openLinksInNewTab,
  };
};

const toConfiguredLink = (
  link: StoreFooterLink,
  context: FooterLinkContext
): PortalFooterLink | undefined => {
  const translationKey = `footer.link.${link.key}`;
  if (link.key === 'refund') return toRefundLink(link.label, translationKey, context, link.url);
  if (link.key === 'support') return toSupportLink(link.label, translationKey, context);
  return toUrlLink(link.key, link.label, translationKey, link.url, context);
};

const toDefaultLinks = (context: FooterLinkContext): (PortalFooterLink | undefined)[] => [
  toUrlLink('privacy', 'Privacy', 'footer.privacy', context.footer.privacyPolicyUrl, context),
  toUrlLink(
    'terms_conditions',
    'T&C',
    'footer.termsAndConditions',
    context.footer.termsAndConditionsUrl,
    context
  ),
  toSupportLink('Support', 'footer.support', context),
  toRefundLink('Refund', 'footer.refund', context),
];

const toUrlLink = (
  key: string,
  label: string,
  translationKey: string,
  url: string | undefined,
  { linkTarget }: FooterLinkContext
): PortalFooterLink | undefined =>
  url ? { key, label, translationKey, url: ensureAbsoluteUrl(url), target: linkTarget } : undefined;

const toRefundLink = (
  label: string,
  translationKey: string,
  { bootData, footer, player }: FooterLinkContext,
  customUrl?: string
): PortalFooterLink | undefined => {
  const playerCountry = player?.playerCountry;
  const isVisible =
    !!bootData.featureFlags?.[EU_REFUND_LINK_FLAG] &&
    !!playerCountry &&
    !!footer.enableRefundFooterLink &&
    !!footer.enabledRefundFooterLinkCountries?.includes(playerCountry);
  if (!isVisible) return undefined;

  const url = customUrl
    ? ensureAbsoluteUrl(customUrl)
    : buildRefundUrl(player?.playerId, bootData.storeTheme.publisherId, playerCountry);
  return { key: 'refund', label, translationKey, url, target: 'newTab' };
};

const toSupportLink = (
  label: string,
  translationKey: string,
  { bootData, footer, player, linkTarget }: FooterLinkContext
): PortalFooterLink => {
  const isLoggedIn = Boolean(player);
  const supportLink = getCustomSupportLink(bootData, footer);
  const hasPostLoginSnippet = !!bootData.supportConfiguration?.postLoginSnippet;

  if (supportLink?.url) {
    return {
      key: 'support',
      label,
      translationKey,
      url: buildSupportUrl(supportLink.url, supportLink.config, player),
      target: linkTarget,
    };
  }

  if (footer.supportLink && (!hasPostLoginSnippet || !isLoggedIn)) {
    return {
      key: 'support',
      label,
      translationKey,
      url: buildSupportUrl(footer.supportLink, footer.supportParams, player),
      target: linkTarget,
    };
  }

  const supportType = isLoggedIn ? ESupportType.POST_LOGIN : ESupportType.PRE_LOGIN;
  const phase = isLoggedIn ? EStorePhase.POST_LOGIN : EStorePhase.PRE_LOGIN;
  return {
    key: 'support',
    label,
    translationKey,
    url: `/support/${supportType}/${phase}`,
    target: 'page',
  };
};

const getCustomSupportLink = (
  bootData: BootData,
  footer: StoreFooter
): StoreFooterLink | undefined => {
  if (!bootData.featureFlags?.[FOOTER_LINKS_MANAGER_FLAG]) return undefined;
  const supportLink = footer.links?.find((link) => link.key === 'support');
  return supportLink?.config?.isCustom ? supportLink : undefined;
};

const buildRefundUrl = (
  playerId?: string,
  publisherId?: string,
  playerCountry?: string
): string => {
  const url = new URL(REFUND_BASE_URL);
  url.searchParams.set(REFUND_TOPIC_PARAM, REFUND_TOPIC_VALUE);
  if (playerId) url.searchParams.set(REFUND_PLAYER_ID_PARAM, playerId);
  if (publisherId) url.searchParams.set(REFUND_PROJECT_ID_PARAM, publisherId);
  if (playerCountry) url.searchParams.set(REFUND_COUNTRY_PARAM, playerCountry);
  return url.toString();
};

const buildSupportUrl = (
  baseUrl: string,
  supportParams: SupportParams | undefined,
  player: FooterPlayer | null
): string => {
  try {
    const url = new URL(ensureAbsoluteUrl(baseUrl));
    if (supportParams?.playerIdParamName && player?.playerId) {
      url.searchParams.set(supportParams.playerIdParamName, player.playerId);
    }
    if (supportParams?.playerNameParamName && player?.playerName) {
      url.searchParams.set(supportParams.playerNameParamName, player.playerName);
    }
    return url.toString();
  } catch {
    return ensureAbsoluteUrl(baseUrl);
  }
};

const LEGACY_LINK_TEST_IDS: Record<string, string> = {
  privacy: 'PortalFooter-PrivacyLink',
  terms_conditions: 'PortalFooter-TermsLink',
  support: 'PortalFooter-SupportLink',
};

export const getFooterLinkTestId = (key: string): string =>
  LEGACY_LINK_TEST_IDS[key] ?? `PortalFooter-Link-${key}`;
